import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';

const API = `${API_BASE}/admin`;
const TAKE = 50;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmt(val) {
  return val && val !== 'NA' ? val : '—';
}

export default function FacilityBoard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ skip: page * TAKE, take: TAKE });
      if (search) params.set('employeeId', search);
      const res = await fetch(`${API}/equipment-assignments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) { navigate('/admin'); return; }
      const data = await res.json();
      setRows(data.assignments || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError('Cannot reach server. Please wait and retry.');
    } finally {
      setLoading(false);
    }
  }, [page, search, token, navigate]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(filterInput);
  };

  const visible = rows.filter(r => {
    if (filterStatus === 'pending')   return r.receiveMethod === 'delivery' && !r.deliveryConfirmed && !r.returned;
    if (filterStatus === 'processed') return r.receiveMethod === 'delivery' && r.deliveryConfirmed && !r.returned;
    if (filterStatus === 'returned')  return r.receiveMethod === 'delivery' && r.returned;
    return r.receiveMethod === 'delivery';
  });

  const pendingCount   = rows.filter(r => r.receiveMethod === 'delivery' && !r.deliveryConfirmed && !r.returned).length;
  const processedCount = rows.filter(r => r.receiveMethod === 'delivery' && r.deliveryConfirmed && !r.returned).length;
  const returnedCount  = rows.filter(r => r.receiveMethod === 'delivery' && r.returned).length;

  const markAsProcessed = async (id) => {
    try {
      const res = await fetch(`${API}/equipment-assignments/${id}/confirm-delivery`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) fetchRows();
    } catch (e) {
      console.error('[FacilityBoard] Failed to mark as processed:', e);
    }
  };

  const badge = (color) => ({
    display: 'inline-block', padding: '4px 14px', borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    background: color === 'green' ? '#e8f5e9' : color === 'orange' ? '#fff3e0' : '#f3f4f6',
    color: color === 'green' ? '#2e7d32' : color === 'orange' ? '#e65100' : '#555'
  });

  return (
    <div className="admin-container" style={{ fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <button
          onClick={() => navigate('/admin')}
          className="btn-back-nav"
        >
          ← Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>🚚 Facility Delivery Board</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
            Delivery coordination — employee contact details and addresses
          </p>
        </div>
        <button
          onClick={fetchRows}
          style={{ marginLeft: 'auto', padding: '9px 16px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'pending',   label: `⏳ Waiting to be Processed (${pendingCount})`,  color: '#f59e0b' },
          { key: 'processed', label: `✅ Processed (${processedCount})`,               color: '#22c55e' },
          { key: 'returned',  label: `↩ Returned (${returnedCount})`,                  color: '#6b7280' },
        ].map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setFilterStatus(key)}
            style={{
              padding: '9px 20px', borderRadius: 20, border: `2px solid ${filterStatus === key ? color : '#e0e0e0'}`,
              background: filterStatus === key ? color : '#fff', color: filterStatus === key ? '#fff' : '#555',
              fontWeight: 600, fontSize: 13, cursor: 'pointer'
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        <input
          type="text"
          placeholder="Search by Employee ID…"
          value={filterInput}
          onChange={e => setFilterInput(e.target.value)}
          style={{ flex: 1, padding: '10px 16px', border: '1px solid #ccc', borderRadius: 7, fontSize: 14 }}
        />
        <button type="submit" style={{ padding: '10px 22px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={() => { setSearch(''); setFilterInput(''); setPage(0); }}
            style={{ padding: '10px 16px', background: '#e0e0e0', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
            Clear
          </button>
        )}
      </form>

      {error && (
        <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
          ⚠ {error} <button onClick={fetchRows} style={{ marginLeft: 10, cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No records found.</div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Showing {visible.length} record{visible.length !== 1 ? 's' : ''}</p>

          {/* Card View (primary) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {visible.map(r => (
              <div key={r.id} style={{
                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12,
                padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex', flexDirection: 'column', gap: 6,
                borderLeft: `4px solid ${r.deliveryConfirmed ? '#22c55e' : r.returned ? '#aaa' : '#f59e0b'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 16, color: '#222' }}>{r.employeeName}</strong>
                  {r.returned ? (
                    <span style={badge('grey')}>Returned</span>
                  ) : r.deliveryConfirmed ? (
                    <span style={badge('green')}>✔ Processed</span>
                  ) : (
                    <span style={badge('orange')}>⏳ Waiting</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 2 }}>
                  <div><strong>Employee ID:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{r.employeeId}</span></div>
                  <div><strong>Department:</strong> {fmt(r.departmentName || r.department?.name)}</div>
                  {r.telephone && (
                    <div><strong>Telephone:</strong> <a href={`tel:${r.telephone}`} style={{ color: '#1a237e', textDecoration: 'none', fontWeight: 500 }}>{r.telephone}</a></div>
                  )}
                  {r.employeeEmail && (
                    <div><strong>Email:</strong> <a href={`mailto:${r.employeeEmail}`} style={{ color: '#1a237e', textDecoration: 'none' }}>{r.employeeEmail}</a></div>
                  )}
                  {r.address && <div><strong>Delivery Address:</strong> {r.address}</div>}
                  <div><strong>Date:</strong> {fmtDate(r.createdAt)}</div>
                </div>

                {!r.deliveryConfirmed && !r.returned && (
                  <button
                    onClick={() => markAsProcessed(r.id)}
                    style={{
                      marginTop: 8, padding: '8px 16px', background: '#22c55e', color: '#fff',
                      border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, width: '100%'
                    }}
                  >
                    ✔ Mark as Processed
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', gap: 8, marginTop: 20, alignItems: 'center' }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #ddd', cursor: page === 0 ? 'not-allowed' : 'pointer', background: page === 0 ? '#f9f9f9' : '#fff' }}>
              ← Prev
            </button>
            <span style={{ fontSize: 13, color: '#666' }}>Page {page + 1} · {total} total</span>
            <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * TAKE >= total}
              style={{ padding: '8px 18px', borderRadius: 6, border: '1px solid #ddd', cursor: (page + 1) * TAKE >= total ? 'not-allowed' : 'pointer', background: (page + 1) * TAKE >= total ? '#f9f9f9' : '#fff' }}>
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
